import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import {
    Plug, CheckCircle2, AlertCircle, Eye, EyeOff, Save, Send,
    FileText, Mail, MessageSquare
} from "lucide-react";
import { toast } from "sonner";
import HelpTip from "@/components/HelpTip";
import { SECTION_HELP, INTEGRATION_HELP } from "@/lib/helpTexts";

const PROVIDER_META = {
    ecofactura: {
        icon: FileText,
        color: "border-l-blue-500",
        description: "Timbrado CFDI 4.0 ante el SAT a través de Ecofactura. Permite emitir facturas reales.",
        helpUrl: "https://ecofactura.mx",
        labels: {
            user: "Usuario portal",
            password: "Contraseña",
            api_key: "Token / API Key",
            rfc_emisor: "RFC emisor (FindMed)",
            regimen_fiscal: "Régimen fiscal (Ej: 612)",
            lugar_expedicion: "Lugar de expedición (CP)",
        },
        testFields: { note: "Verifica que las claves estén guardadas (no timbra realmente)" },
    },
    resend: {
        icon: Mail,
        color: "border-l-emerald-500",
        description: "Envío de email transaccional (estado de cuenta, pre-avisos a hospitales, recordatorios de cobranza).",
        helpUrl: "https://resend.com",
        labels: {
            api_key: "API Key (re_…)",
            from_email: "Email remitente (verificado en Resend)",
            from_name: "Nombre remitente",
            reply_to: "Reply-to (opcional)",
        },
        testFields: { to: "Email destino para prueba" },
    },
    twilio: {
        icon: MessageSquare,
        color: "border-l-amber-500",
        description: "SMS y WhatsApp para pre-avisos a hospitales y notificaciones al personal.",
        helpUrl: "https://twilio.com",
        labels: {
            account_sid: "Account SID",
            auth_token: "Auth Token",
            from_number: "Número saliente (E.164: +52...)",
            whatsapp_from: "WhatsApp From (opcional)",
        },
        testFields: { to: "Número destino (E.164: +52...)", channel: "Canal: sms | whatsapp" },
    },
};

function ProviderCard({ provider, meta, status, onSaved }) {
    const ProviderIcon = PROVIDER_META[provider]?.icon || Plug;
    const localMeta = PROVIDER_META[provider] || {};
    const [values, setValues] = useState({});
    const [show, setShow] = useState({});
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testTo, setTestTo] = useState("");
    const [lastTest, setLastTest] = useState(null);

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.post(`/integrations/${provider}`, values);
            toast.success("Configuración guardada");
            setValues({}); // limpiar inputs
            onSaved?.();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Error al guardar");
        } finally {
            setSaving(false);
        }
    };

    const runTest = async () => {
        setTesting(true);
        setLastTest(null);
        try {
            const body = { to: testTo, channel: "sms" };
            const { data } = await api.post(`/integrations/test/${provider}`, body);
            setLastTest(data);
            if (data.mocked) {
                toast.warning("Modo simulado · " + (data.note || data.error || "Configura las claves para envío real"));
            } else if (data.ok === false) {
                toast.error("Error: " + (data.error || "ver detalles"));
            } else {
                toast.success("Prueba enviada");
            }
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Error en prueba");
        } finally {
            setTesting(false);
        }
    };

    return (
        <div className={`fm-card p-5 border-l-4 ${localMeta.color || ""}`} data-testid={`provider-${provider}`}>
            <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-slate-900 text-white rounded-sm flex items-center justify-center">
                        <ProviderIcon className="w-5 h-5" />
                    </div>
                    <div>
                        <h3 className="font-display font-bold text-base flex items-center gap-1">
                            {meta.label}
                            {INTEGRATION_HELP[provider] && <HelpTip text={INTEGRATION_HELP[provider]} />}
                        </h3>
                        <p className="text-xs text-slate-500 max-w-xl">{localMeta.description}</p>
                        {localMeta.helpUrl && (
                            <a href={localMeta.helpUrl} target="_blank" rel="noreferrer" className="text-xs text-red-600 hover:underline mt-0.5 inline-block">
                                Documentación oficial →
                            </a>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {status.configured
                        ? <span className="fm-badge fm-badge-positive inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Configurado</span>
                        : <span className="fm-badge fm-badge-warning inline-flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Sin configurar</span>}
                </div>
            </div>

            <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                {meta.fields.map((f) => {
                    const isSecret = ["password", "api_key", "auth_token", "secret"].some((k) => f.toLowerCase().includes(k));
                    return (
                        <div key={f}>
                            <label className="fm-label flex items-center justify-between">
                                <span>{localMeta.labels?.[f] || f}</span>
                                {status.fields_present?.[f] && <span className="text-emerald-600 text-[10px]">guardado</span>}
                            </label>
                            <div className="relative">
                                <input
                                    type={isSecret && !show[f] ? "password" : "text"}
                                    value={values[f] ?? ""}
                                    onChange={(e) => setValues({ ...values, [f]: e.target.value })}
                                    placeholder={status.fields_present?.[f] ? "•••••• (dejar vacío para no cambiar)" : ""}
                                    className="fm-input pr-9"
                                    data-testid={`field-${provider}-${f}`}
                                    autoComplete="off"
                                />
                                {isSecret && (
                                    <button
                                        type="button"
                                        onClick={() => setShow({ ...show, [f]: !show[f] })}
                                        data-testid={`toggle-secret-${provider}-${f}`}
                                        aria-label={show[f] ? "Ocultar" : "Mostrar"}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                                    >
                                        {show[f] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}

                <div className="md:col-span-2 flex items-center justify-between gap-3 mt-2 flex-wrap">
                    <div className="text-[10px] text-slate-400 uppercase tracking-widest">
                        {status.updated_at && <>Última actualización: {status.updated_at.slice(0, 16).replace("T", " ")} por {status.updated_by_name}</>}
                    </div>
                    <button type="submit" disabled={saving} className="fm-btn-accent" data-testid={`save-${provider}-btn`}>
                        <Save className="w-3.5 h-3.5" /> {saving ? "Guardando..." : "Guardar claves"}
                    </button>
                </div>
            </form>

            {/* Test connection */}
            <div className="border-t border-slate-200 mt-5 pt-4">
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Probar integración</div>
                <div className="flex items-end gap-2 flex-wrap">
                    {provider !== "ecofactura" && (
                        <div className="flex-1 min-w-[200px]">
                            <input
                                value={testTo}
                                onChange={(e) => setTestTo(e.target.value)}
                                placeholder={localMeta.testFields?.to || "destino"}
                                className="fm-input text-xs"
                                data-testid={`test-to-${provider}`}
                            />
                        </div>
                    )}
                    <button type="button" onClick={runTest} disabled={testing} className="fm-btn-outline" data-testid={`test-${provider}-btn`}>
                        <Send className="w-3.5 h-3.5" /> {testing ? "Enviando..." : "Probar"}
                    </button>
                </div>
                {lastTest && (
                    <pre className={`text-[11px] font-mono p-3 mt-3 rounded-sm overflow-auto ${lastTest.mocked ? "bg-amber-50 border border-amber-300" : lastTest.ok === false ? "bg-red-50 border border-red-300" : "bg-emerald-50 border border-emerald-300"}`}>
                        {JSON.stringify(lastTest, null, 2)}
                    </pre>
                )}
            </div>
        </div>
    );
}

export default function Integrations() {
    const [data, setData] = useState(null);
    const [unauthorized, setUnauthorized] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/integrations");
            setData(data);
        } catch (e) {
            if (e?.response?.status === 403) setUnauthorized(true);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    if (unauthorized) {
        return (
            <div className="fm-card p-10 text-center" data-testid="integrations-unauthorized">
                <Plug className="w-12 h-12 mx-auto mb-3 text-red-400" strokeWidth={1.5} />
                <h2 className="font-display font-bold text-lg mb-1">Acceso restringido</h2>
                <p className="text-sm text-slate-500">Solo superadmin o dirección puede configurar integraciones.</p>
            </div>
        );
    }

    if (!data) return <div className="text-sm text-slate-500 font-mono">Cargando integraciones...</div>;

    return (
        <div className="space-y-4 max-w-5xl" data-testid="integrations-page">
            <div>
                <h2 className="font-display font-black text-2xl tracking-tight flex items-center gap-2">
                    <Plug className="w-6 h-6 text-red-600" /> Integraciones
                    <HelpTip text={SECTION_HELP.integrations_hub} variant="?" />
                </h2>
                <div className="text-xs text-slate-500 font-mono">Conecta proveedores externos · Las claves se guardan cifradas</div>
            </div>

            <div className="fm-card p-4 bg-amber-50 border-l-4 border-l-amber-500 flex items-start gap-3 text-xs">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                    <strong className="text-amber-900">Modo seguro · todas las claves se almacenan cifradas con AES-128.</strong>
                    <br />Mientras no configures un proveedor, el sistema funciona en <b>modo simulado</b>: los envíos quedan registrados con marca <code className="bg-amber-100 px-1 rounded">mocked: true</code> pero NO se entregan realmente.
                </div>
            </div>

            <div className="space-y-4">
                {Object.entries(data).map(([prov, meta]) => (
                    <ProviderCard key={prov} provider={prov} meta={meta} status={meta} onSaved={load} />
                ))}
            </div>
        </div>
    );
}
